import { ApiResult, HttpClient } from "../http.js";
import type {
  KlsAiAutoFillInspectionDto,
  KlsAiAvailabilityDto,
  KlsAiCorrectiveActionsDto,
  KlsAiInspectionType,
  KlsAiInstallationType,
  KlsAiPreFillVerificationDto,
  KlsAiReportSummaryDto,
  KlsAiReportType,
} from "../types/kls-ai.js";

export interface AutoFillKlsInspectionPayload {
  inspectionType: KlsAiInspectionType;
  projectContext?: string | null;
}

export interface PreFillKlsVerificationPayload {
  installationType: KlsAiInstallationType;
  projectContext?: string | null;
}

export interface GenerateKlsReportSummaryPayload {
  reportType: KlsAiReportType;
}

/**
 * `/api/kls/ai/*` — KLS AI assistance (inspection auto-fill, corrective-action
 * suggestions, verification pre-fill, report summaries).
 *
 * The Anthropic key is server-side only: the platform key from configuration, or the
 * org's Key Vault-backed BYO key. It is never sent from, or returned to, a client.
 * Generation consumes one credit from the shared AI-credit pool unless the org runs on
 * its own key — a 402 means the balance is exhausted.
 */
export class KlsAiApi {
  constructor(private readonly http: HttpClient) {}

  /**
   * GET /api/kls/ai/availability
   * Whether AI is usable for the caller's org, on whose key, and the remaining credits.
   */
  availability(): Promise<ApiResult<KlsAiAvailabilityDto>> {
    return this.http.get<KlsAiAvailabilityDto>("/api/kls/ai/availability");
  }

  /**
   * POST /api/kls/ai/projects/{projectId}/inspection-autofill
   * Generates a DS/HD 60364-6 inspection checklist for the project.
   */
  autoFillInspection(
    projectId: string,
    payload: AutoFillKlsInspectionPayload,
  ): Promise<ApiResult<KlsAiAutoFillInspectionDto>> {
    return this.http.post<KlsAiAutoFillInspectionDto>(
      `/api/kls/ai/projects/${encodeURIComponent(projectId)}/inspection-autofill`,
      { body: payload },
    );
  }

  /**
   * POST /api/kls/ai/projects/{projectId}/verification-prefill
   * Suggests a filled-in verification schema for the project's installation type.
   */
  preFillVerification(
    projectId: string,
    payload: PreFillKlsVerificationPayload,
  ): Promise<ApiResult<KlsAiPreFillVerificationDto>> {
    return this.http.post<KlsAiPreFillVerificationDto>(
      `/api/kls/ai/projects/${encodeURIComponent(projectId)}/verification-prefill`,
      { body: payload },
    );
  }

  /**
   * POST /api/kls/ai/projects/{projectId}/report-summary
   * Generates a KLS report body from the project's tasks, inspections and deviations.
   */
  generateReportSummary(
    projectId: string,
    payload: GenerateKlsReportSummaryPayload,
  ): Promise<ApiResult<KlsAiReportSummaryDto>> {
    return this.http.post<KlsAiReportSummaryDto>(
      `/api/kls/ai/projects/${encodeURIComponent(projectId)}/report-summary`,
      { body: payload },
    );
  }

  /**
   * POST /api/kls/ai/deviations/{deviationId}/corrective-actions
   * Suggests corrective + preventive actions for an existing deviation. The deviation is
   * read server-side by id — unlike the Cloud Function, the caller does not supply its
   * own copy of the title/description/severity.
   */
  suggestCorrectiveActions(
    deviationId: string,
  ): Promise<ApiResult<KlsAiCorrectiveActionsDto>> {
    return this.http.post<KlsAiCorrectiveActionsDto>(
      `/api/kls/ai/deviations/${encodeURIComponent(deviationId)}/corrective-actions`,
    );
  }
}
